import React, { useMemo, useState } from "react";
import { getScheduleSectionLabel } from "../projectsScheduleHelpers";

const ALL_SECTION_VALUE = "__all_section__";

const sameId = (left, right) => String(left) === String(right);

const hasId = (ids, id) => (ids || []).some((item) => sameId(item, id));

const getSectionLabel = (section) => getScheduleSectionLabel(section);

const WorkTypeRelationPicker = ({
  label,
  icon: Icon,
  selectedIds = [],
  onSelectedIdsChange,
  excludedIds = [],
  currentWorkTypeId,
  sections = [],
  getWorkTypeLabel,
  loading = false,
  disabled = false,
  readOnly = false,
}) => {
  const [sectionId, setSectionId] = useState("");

  const selectedSection = useMemo(
    () => sections.find((section) => sameId(section.id, sectionId)),
    [sections, sectionId],
  );

  const availableWorkTypes = useMemo(() => {
    if (!selectedSection) return [];
    return (selectedSection.work_types || []).filter(
      (wt) =>
        !sameId(wt.id, currentWorkTypeId) &&
        !hasId(selectedIds, wt.id) &&
        !hasId(excludedIds, wt.id),
    );
  }, [selectedSection, currentWorkTypeId, selectedIds, excludedIds]);

  const formatOptionLabel = (wt) =>
    wt.is_milestone ? `◆ ${wt.name}` : wt.name;

  const handleSectionChange = (e) => {
    setSectionId(e.target.value);
  };

  const addWorkTypeIds = (ids) => {
    const unique = ids.filter(
      (id) =>
        !sameId(id, currentWorkTypeId) &&
        !hasId(selectedIds, id) &&
        !hasId(excludedIds, id),
    );
    if (unique.length === 0) return;
    onSelectedIdsChange([...selectedIds, ...unique]);
    setSectionId("");
  };

  const handleWorkTypeChange = (e) => {
    const value = e.target.value;
    if (!value) return;

    if (value === ALL_SECTION_VALUE) {
      addWorkTypeIds(availableWorkTypes.map((wt) => wt.id));
      return;
    }

    const selected = availableWorkTypes.find((wt) => sameId(wt.id, value));
    if (!selected) return;
    addWorkTypeIds([selected.id]);
  };

  const removeId = (id) => {
    onSelectedIdsChange(selectedIds.filter((item) => !sameId(item, id)));
  };

  const pickerDisabled =
    disabled || readOnly || loading || sections.length === 0;

  return (
    <div className="edit-wt-drawer__relation">
      <div className="edit-wt-drawer__relation-label">
        {Icon ? <Icon /> : null}
        {label}
      </div>

      {selectedIds.length > 0 ? (
        <div className="edit-wt-drawer__chips">
          {selectedIds.map((id) => (
            <span key={String(id)} className="edit-wt-drawer__chip">
              {getWorkTypeLabel(id)}
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => removeId(id)}
                  disabled={disabled}
                  aria-label="Удалить"
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      ) : readOnly ? (
        <div className="edit-wt-drawer__readonly-value">Не задано</div>
      ) : null}

      {!readOnly && (
        <div className="edit-wt-drawer__relation-pickers">
          <select
            className="edit-wt-drawer__relation-select"
            value={sectionId}
            disabled={pickerDisabled}
            onChange={handleSectionChange}
          >
            <option value="">
              {loading ? "Загрузка разделов..." : "+ Выберите раздел"}
            </option>
            {sections.map((section) => (
              <option key={String(section.id)} value={String(section.id)}>
                {getSectionLabel(section)}
              </option>
            ))}
          </select>

          <select
            className="edit-wt-drawer__relation-select"
            value=""
            disabled={pickerDisabled || !sectionId || availableWorkTypes.length === 0}
            onChange={handleWorkTypeChange}
          >
            <option value="">
              {!sectionId
                ? "Сначала выберите раздел"
                : availableWorkTypes.length === 0
                  ? "Нет доступных работ/вех"
                  : "+ Выберите работу или веху"}
            </option>
            {availableWorkTypes.length > 0 && (
              <option value={ALL_SECTION_VALUE}>Весь раздел</option>
            )}
            {availableWorkTypes.map((wt) => (
              <option key={String(wt.id)} value={String(wt.id)}>
                {formatOptionLabel(wt)}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default WorkTypeRelationPicker;
